import {
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import dayjs from "dayjs";
import React from "react";
import { useHistory } from "react-router";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import apiHelper from "../../../../util/ApiHelper";
import { useLoading } from "../../../../util/LoadingProvider";

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
}));

const imgUrlPrefix =
  process.env.REACT_APP_HKIFF_REFUND_IMG_PREFIX ||
  "https://hkiff-website-dev.cloud.udev.hk";

function RefundAppEditPage(props) {
  const history = useHistory();
  const classes = useStyles();
  const { match, disabled, location } = props;
  const { id } = match.params;
  const [defaultValue, setDefaultValue] = React.useState({});
  const pageIndex = (location.state && location.state.pageIndex) || 0;
  const pageSize = (location.state && location.state.pageSize) || 10;
  const { setLoading } = useLoading();
  const [isSendEmail, setIsSendEmail] = React.useState(false);

  const { control, register, handleSubmit, reset } = useForm({
    defaultValues: React.useMemo(() => defaultValue, [defaultValue]),
  });

  const getApplicationDetail = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getRefundAppFormById(id);
      setDefaultValue(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [id, reset]);

  const updateRefundAppForm = async (data) => {
    try {
      setLoading(true);
      data.sendEmail = isSendEmail;
      const result = await apiHelper.hkiff.updateRefundAppFormById(id, data);
      if (result.data.id) {
        toast.dark(isSendEmail? "Your changes is saved and email sent." : "Your changes is saved.");
        history.push({
          pathname: "/hkiff/refund/application-list",
          state: { pageIndex, pageSize },
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (value) => {
    console.log(value);
    const data = { events: value.events, status: value.status };
    updateRefundAppForm(data);
  };

  React.useEffect(() => {
    getApplicationDetail();
  }, [getApplicationDetail]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Refund &gt; Application Details
      </Typography>
      {defaultValue.events && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={disabled} className="fieldset">
            <Box className={classes.innerBox}>
              <Box className={classes.sectionBox}>
                <Grid container xs={12} direction="row">
                  <Grid item xs={6}>
                    <strong>Reference no. #{defaultValue.referenceNum}</strong>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      width="100%"
                      component="span"
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <strong>Last Update Time &nbsp; </strong>
                      {(defaultValue.updateTime &&
                        dayjs(defaultValue.updateTime).format(
                          "DD MMM YYYY, h:mma"
                        )) ||
                        dayjs(defaultValue.createTime).format(
                          "DD MMM YYYY, h:mma"
                        )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Divider />
              {/* ENTRY Detail */}
              <Box className={classes.sectionBox}>
                <Grid container xs={12} direction="row" spacing={3}>
                  <Grid container item xs={12} direction="row">
                    <Typography className={classes.sectionTitle}>
                      Refund Details
                    </Typography>
                  </Grid>
                  <Grid container item xs={12} direction="row" spacing={3}>
                    <Grid container item xs={12} direction="row" spacing={3}>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Email Address
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.email}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Refund Programme
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.programme &&
                              defaultValue.programme.localizations.en.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} direction="row" spacing={3}>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Patron's Name
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.patronName}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Contact Number
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.contactNumber}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Correspondence Address
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.address}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} direction="row" spacing={3}>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={4}
                      >
                        <Grid item>
                          <Typography className={classes.itemTitle}>
                            Redemption Method
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.itemValue}>
                            {defaultValue.redemptionMethod}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        item
                        direction="column"
                        spacing={1}
                        xs={8}
                      >
                        <Grid item style={{ marginBottom: "3px" }}>
                          <Typography className={classes.itemTitle}>
                            Application Status
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Controller
                            control={control}
                            name="status"
                            render={({
                              field: { value, onChange, ...rest },
                              fieldState,
                            }) => (
                              <ToggleButtonGroup
                                {...rest}
                                value={value || defaultValue.status}
                                onChange={(event, newFormats) => {
                                  onChange(newFormats);
                                }}
                                exclusive
                                aria-label="Application status"
                                className={classes.input}
                              >
                                <ToggleButton
                                  value={1}
                                  aria-label="Pending"
                                  disabled={disabled}
                                >
                                  Pending
                                </ToggleButton>
                                <ToggleButton
                                  value={2}
                                  aria-label="Confirmed"
                                  disabled={disabled}
                                >
                                  Completed
                                </ToggleButton>
                                <ToggleButton
                                  value={3}
                                  aria-label="Duplicated"
                                  disabled={disabled}
                                >
                                  Duplicated
                                </ToggleButton>
                              </ToggleButtonGroup>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {defaultValue.redemptionMethod === "Cheque" && (
                      <Grid container item xs={12} direction="row" spacing={3}>
                        <Grid
                          container
                          item
                          direction="column"
                          spacing={1}
                          xs={4}
                        >
                          <Grid item>
                            <Typography className={classes.itemTitle}>
                              Payable To
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.itemValue}>
                              {defaultValue.payableTo || "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          direction="column"
                          spacing={1}
                          xs={8}
                        >
                          <Grid item style={{ marginBottom: "3px" }}>
                            <Typography className={classes.itemTitle}>
                              Mail Method
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.itemValue}>
                              {defaultValue.mailMethod || "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}

                    {defaultValue.redemptionMethod === "Bank Transfer" && (
                      <Grid container item xs={12} direction="row" spacing={3}>
                        <Grid
                          container
                          item
                          direction="column"
                          spacing={1}
                          xs={4}
                        >
                          <Grid item>
                            <Typography className={classes.itemTitle}>
                              Bank Code
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.itemValue}>
                              {defaultValue.bankCode || "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          direction="column"
                          spacing={1}
                          xs={4}
                        >
                          <Grid item style={{ marginBottom: "3px" }}>
                            <Typography className={classes.itemTitle}>
                              Account Number
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.itemValue}>
                              {defaultValue.accountNo || "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid
                          container
                          item
                          direction="column"
                          spacing={1}
                          xs={4}
                        >
                          <Grid item style={{ marginBottom: "3px" }}>
                            <Typography className={classes.itemTitle}>
                              Account Holder Name
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography className={classes.itemValue}>
                              {defaultValue.accountHolderName || "-"}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Box>
              {defaultValue.events.map((event, index) => {
                return (
                  <>
                    <Divider />
                    {/* Refund Programmes */}
                    <input
                      value={event.id}
                      type="hidden"
                      {...register(`events.${index}.id`)}
                    />
                    <Box className={classes.sectionBox}>
                      <Grid container xs={12} direction="row" spacing={3}>
                        <Grid container item xs={12} direction="row">
                          <Typography className={classes.sectionTitle}>
                            Refund Programme
                          </Typography>
                        </Grid>
                        <Grid
                          container
                          item
                          xs={12}
                          direction="row"
                          spacing={3}
                        >
                          <Grid
                            container
                            item
                            xs={12}
                            direction="row"
                            spacing={3}
                          >
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Event Name
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.eventName}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Screening Date
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.screeningDate}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Screening Time
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.screeningTime}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid
                            container
                            item
                            xs={12}
                            direction="row"
                            spacing={3}
                          >
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Screening Venue
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.screeningVenue}
                                </Typography>
                              </Grid>
                            </Grid>
                            {/* <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Seat No.
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.seatNo}
                                </Typography>
                              </Grid>
                            </Grid> */}
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  No. of Ticket(s)
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.ticketNumber}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid
                            container
                            item
                            xs={12}
                            direction="row"
                            spacing={3}
                          >
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Price of Ticket(s)
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.ticketPrice}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Total Amount
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.totalAmount}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Reason for Refund
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.refundReason}
                                </Typography>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Screening Incident
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography className={classes.itemValue}>
                                  {event.screeningIncident}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Grid>

                          <Grid
                            container
                            item
                            xs={12}
                            direction="row"
                            spacing={1}
                          >
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Photocopy of Ticket(s)
                                </Typography>
                              </Grid>
                              <Grid item>
                                <a
                                  href={`${imgUrlPrefix}${event.ticketPhotocopy}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <img
                                    style={{
                                      maxWidth: "90px",
                                      maxHeight: "90px",
                                    }}
                                    src={`${imgUrlPrefix}${event.ticketPhotocopy}`}
                                    alt=""
                                  />
                                </a>
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              item
                              direction="column"
                              spacing={1}
                              xs={4}
                            >
                              <Grid item>
                                <Typography className={classes.itemTitle}>
                                  Redemption Status
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Controller
                                  control={control}
                                  name={`events[${index}].redemptionStatus`}
                                  render={({
                                    field: { value, onChange, ...rest },
                                    fieldState,
                                  }) => (
                                    <ToggleButtonGroup
                                      {...rest}
                                      value={value || event.redemptionStatus}
                                      onChange={(event, newFormats) => {
                                        onChange(newFormats);
                                      }}
                                      exclusive
                                      aria-label="Redemption status"
                                      className={classes.input}
                                    >
                                      <ToggleButton
                                        value="PENDING"
                                        aria-label="Pending"
                                        disabled={disabled}
                                      >
                                        Pending
                                      </ToggleButton>
                                      <ToggleButton
                                        value="APPROVED"
                                        aria-label="Approved"
                                        disabled={disabled}
                                      >
                                        Approved
                                      </ToggleButton>
                                      <ToggleButton
                                        value="VOUCHER"
                                        aria-label="Voucher"
                                        disabled={disabled}
                                      >
                                        Voucher
                                      </ToggleButton>
                                      <ToggleButton
                                        value="REJECTED"
                                        aria-label="Rejected"
                                        disabled={disabled}
                                      >
                                        Rejected
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                );
              })}
            </Box>
            {disabled || (
              <div className={classes.buttonGroup}>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                  onClick={() => setIsSendEmail(false)}
                >
                  Save Changes
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  style={{
                    color: "white",
                    background: "#306F9D",
                    marginRight: "5px",
                    padding: "9px 16px",
                  }}
                  onClick={() => setIsSendEmail(true)}
                >
                  Save and send email
                </Button>
              </div>
            )}
          </fieldset>
        </form>
      )}
    </Box>
  );
}

export default RefundAppEditPage;
